import { useReactFlow, Handle, Position, NodeResizer } from "@xyflow/react";
import { Select, Button, InputNumber, Input } from "antd";
import { FormOutlined, CloseOutlined, CopyOutlined } from "@ant-design/icons";
import NodeNameInput from "./NodeNameInput";

const CAPTURE_FIELDS = [
  { value: "firstname",   label: "First Name" },
  { value: "lastname",    label: "Last Name" },
  { value: "email",       label: "Email" },
  { value: "phone",       label: "Phone (secondary)" },
  { value: "company",     label: "Company" },
  { value: "designation", label: "Job Title" },
  { value: "notes",       label: "Notes (append)" },
  { value: "website",     label: "Website" },
  { value: "linkedin",    label: "LinkedIn" },
  { value: "instagram",   label: "Instagram" },
  { value: "telegram",    label: "Telegram" },
  { value: "twitter",     label: "Twitter" },
  { value: "facebook",    label: "Facebook" },
  { value: "attachment",  label: "Attachment (image/doc/video)" },
];

const COLOR = "#06b6d4";

const CaptureInputNode = ({ data, selected, id }) => {
  const { deleteElements } = useReactFlow();

  const selectedField = CAPTURE_FIELDS.find((f) => f.value === data.field);

  return (
    <div style={{
      background: "#fff",
      border: `2px solid ${selected ? COLOR : "#e5e7eb"}`,
      borderRadius: 10,
      padding: 14,
      minWidth: 260,
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      width: "100%",
      height: "100%",
      boxSizing: "border-box",
    }}>
      <NodeResizer minWidth={240} minHeight={80} isVisible={selected} color={COLOR} />
      <Handle type="target" position={Position.Left} style={{ background: COLOR }} />

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <FormOutlined style={{ color: COLOR, fontSize: 16 }} />
        <strong style={{ fontSize: 13 }}>Capture Input</strong>
        <Button
          type="text" size="small" icon={<CopyOutlined />}
          onClick={() => data.onDuplicate?.()}
          style={{ marginLeft: "auto", color: "#9ca3af" }}
        />
        <Button
          type="text" size="small" icon={<CloseOutlined />}
          onClick={() => deleteElements({ nodes: [{ id }] })}
          style={{ color: "#9ca3af" }}
        />
      </div>

      <div className="nodrag" onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <NodeNameInput data={data} />
        <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
          Save reply as
        </label>
        <Select
          value={data.field || undefined}
          onChange={(v) => data.onChange?.({ ...data, field: v })}
          size="small"
          style={{ width: "100%" }}
          placeholder="Select field to save"
          options={CAPTURE_FIELDS}
        />
      </div>

      {data.field === "attachment" && (
        <div className="nodrag" onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} style={{ marginTop: 10 }}>
          <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
            Max file size (MB) — leave blank for no limit
          </label>
          <InputNumber
            min={1}
            max={100}
            value={data.maxSizeMB || null}
            onChange={(v) => data.onChange?.({ ...data, maxSizeMB: v || null })}
            size="small"
            style={{ width: "100%" }}
            placeholder="e.g. 10"
          />
        </div>
      )}

      {["firstname","lastname","company","designation","website","linkedin","instagram","telegram","twitter","facebook"].includes(data.field) && (
        <div className="nodrag" onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} style={{ marginTop: 10 }}>
          <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>Validation (optional)</label>
          <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: "#9ca3af" }}>Min length</label>
              <InputNumber min={1} value={data.minLength || null} onChange={(v) => data.onChange?.({ ...data, minLength: v || null })} size="small" style={{ width: "100%" }} placeholder="–" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: "#9ca3af" }}>Max length</label>
              <InputNumber min={1} value={data.maxLength || null} onChange={(v) => data.onChange?.({ ...data, maxLength: v || null })} size="small" style={{ width: "100%" }} placeholder="–" />
            </div>
          </div>
          <label style={{ fontSize: 10, color: "#9ca3af", display: "block", marginBottom: 2 }}>Regex pattern (optional)</label>
          <Input value={data.validationPattern || ""} onChange={(e) => data.onChange?.({ ...data, validationPattern: e.target.value })} size="small" placeholder="e.g. ^[A-Za-z]+$" style={{ marginBottom: 6 }} />
          <label style={{ fontSize: 10, color: "#9ca3af", display: "block", marginBottom: 2 }}>Custom error message for pattern mismatch</label>
          <Input value={data.validationError || ""} onChange={(e) => data.onChange?.({ ...data, validationError: e.target.value })} size="small" placeholder="e.g. Only letters allowed." />
        </div>
      )}

      <div className="nodrag" onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} style={{ marginTop: 10 }}>
        <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
          Rejection message (sent if save fails)
        </label>
        <Input.TextArea
          value={data.rejectionMessage || ""}
          onChange={(e) => data.onChange?.({ ...data, rejectionMessage: e.target.value })}
          size="small"
          rows={2}
          placeholder="e.g. This number is already saved. Please send a different one."
        />
      </div>

      {selectedField && (
        <div style={{
          marginTop: 10, padding: "6px 10px",
          background: `${COLOR}11`, borderRadius: 6,
          fontSize: 11, color: "#0e7490",
        }}>
          {selectedField.value === "attachment"
            ? `Waits for user to send image / document / video → saves to Attachments tab${data.maxSizeMB ? ` (max ${data.maxSizeMB} MB)` : ""}`
            : <>Waits for user reply → saves to <strong>{selectedField.label}</strong></>
          }
        </div>
      )}

      <div className="nodrag" onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} style={{ marginTop: 10 }}>
        <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 2 }}>
          Also save as variable <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
        </label>
        <Input
          value={data.varName || ""}
          onChange={(e) => data.onChange?.({ ...data, varName: e.target.value.replace(/\s/g, "_") })}
          size="small"
          placeholder="e.g. user_answer"
          style={{ fontSize: 11 }}
        />
        {data.varName && (
          <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>
            Use <code>{`{{${data.varName}}}`}</code> in later nodes
          </div>
        )}
      </div>

      <Handle type="source" position={Position.Right} style={{ background: COLOR }} />
    </div>
  );
};

export default CaptureInputNode;
